import * as Inovx from '@shared';
import AssetSetsServices from '../services/AssetSetsServices';
import FacilityStore from '../stores/FacilityStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import viewerStore from '../stores/ViewerStore';
import AssetSetVisualizationStore from '../stores/AssetSetVisualizationStore';
import FacilityAssetSetStore from '../stores/FacilityAssetSetStore';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var FormHelper = Inovx.Shared.Common.FormHelper;
import KnowledgeBookConstants from './../constants/KnowledgeBookConstants';
import ViewerConstants from './../constants/ViewerConstants';
function _receiveAssetSets(data: IAssetSet[]): void {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.ASSET_SETS_GET_ALL,
        data: data
    });
}

interface AssetSetSaveOptions {
    suppressGlobalError?: boolean;
    validClassificationTagIDs?: string[];
}
function _dispatchAction(actionType: string, data ?: any) {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: actionType,
        data: data
    });
}

function _getValidClassificationTags(classificationTags?: string[], validClassificationTagIDs?: string[]) {
    const taxonomyDetail = FacilityStore.getTaxonomyClassificationDetail(TaxonomyType.AssetSet);
    const validTags = new Set(validClassificationTagIDs ?? (taxonomyDetail?.ClassificationTags ?? []).map(tag => tag.ID));

    return Array.from(new Set(
        (classificationTags ?? []).filter(id => !!id && validTags.has(id))
    ));
}

var AssetSetsActions = {
    openModal(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSET_SET_MODAL_OPEN
        });
    },
    beginAdd(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSET_SET_ADD
        });
    },
    beginEdit(data: IAssetSet): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSET_SET_EDIT,
            data
        });
    },
    updateAssetSetInStore(data: IAssetSet): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSET_SET_UPDATE,
            data
        });
    },
    beginFinish(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSET_SET_FINISH
        });
    },
    beginFacilityAssetSetAdd(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_FACILITY_ASSET_SET_ADD
        });
    },
    beginFacilityAssetSetEdit(data: IAssetSet): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_FACILITY_ASSET_SET_EDIT,
            data
        });
    },
    updateFacilityAssetSetInStore(data: IAssetSet): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_FACILITY_ASSET_SET_UPDATE,
            data
        });
    },
    beginFacilityAssetSetFinish(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_FACILITY_ASSET_SET_FINISH
        });
    },
    async getAssetSetDetail(id: string) {
        const itemDetails = await AssetSetsServices.getAssetSetDetail(id);
        _dispatchAction(KnowledgeBookConstants.UPDATE_ASSET_SET, itemDetails);
        return itemDetails;
    },
    async refreshAllAssetSets() {
        const selectedKnowledgeBook = KnowledgeBookStore.getSelectedKnowledgeBook();
        if (!selectedKnowledgeBook?.ID) {
            return null;
        }

        const selectedAssetSet = viewerStore.getSelectedAssetSet();

        const allAssetSets = await AssetSetsServices.getAssetSets(selectedKnowledgeBook.ID);
        _receiveAssetSets(allAssetSets);

        if (selectedAssetSet?.KnowledgeBookID === selectedKnowledgeBook.ID) {
            const refreshedAssetSet = allAssetSets?.find(asset => asset.ID === selectedAssetSet.ID);
            _dispatchAction(ViewerConstants.ASSET_SET_SELECT, refreshedAssetSet || null);
        }

        return allAssetSets;
    },
    async refreshFacilityAssetSets(facilityID: string, updateStore: boolean = false) {
        if (!facilityID) {
            return [];
        }

        const allAssetSets = await AssetSetsServices.getAssetSetsByFacility(facilityID);
        const selectedAssetSet = viewerStore.getSelectedAssetSet();

        if (selectedAssetSet?.FacilityID === facilityID && !selectedAssetSet.KnowledgeBookID) {
            const refreshedAssetSet = allAssetSets.find(asset => asset.ID === selectedAssetSet.ID);
            if (refreshedAssetSet) {
                _dispatchAction(ViewerConstants.ASSET_SET_SELECT, refreshedAssetSet);
            }
        }

        // Cache for surfaces that need the canonical Facility-wide special sets but do not
        // load Asset Sets themselves (Content Layers, point cloud viewer).
        FacilityAssetSetStore.setAssetSets(facilityID, allAssetSets ?? []);

        if (updateStore) {
            _receiveAssetSets(allAssetSets);
        }

        return allAssetSets;
    },
    async refreshAssetSetsAfterExternalChange(facilityID?: string) {
        await Promise.all([
            AssetSetsActions.refreshAllAssetSets(),
            facilityID
                ? AssetSetsActions.refreshFacilityAssetSets(facilityID, false)
                : Promise.resolve([]),
        ]);

        if (facilityID) {
            AssetSetVisualizationStore.notifyAssetSetsChanged(facilityID);
        }
    },
    finishEdit(assetSet: IAssetSet = null): void {
        if (KnowledgeBookStore.getHasAssetSetChanges()) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: () => {
                    if (KnowledgeBookStore.isAddingAssetSet()) {
                        AssetSetsActions.createAssetSet(assetSet)
                    } else {
                        AssetSetsActions.updateAssetSet(assetSet);
                    }
                },
                cancelCallback: () => {
                    this.beginFinish();
                }
            });
        } else {
            this.beginFinish();
        }
    },
    async refreshAssetSets(knowledgeBookID: string) {
        const data = await AssetSetsServices.getAssetSets(knowledgeBookID);
        _receiveAssetSets(data);
        return data;
    },
    async createAssetSet(assetSet: IAssetSet, options?: AssetSetSaveOptions) {
        const trimData: any = FormHelper.trim(assetSet);
        const form_data: any = new FormData();
        const assetTagIDs = assetSet.AssetTagIDs ?? [];
        const classificationTags = _getValidClassificationTags(assetSet.ClassificationTags, options?.validClassificationTagIDs);

        form_data.append("FacilityID", assetSet.FacilityID);
        if (assetSet.KnowledgeBookID) {
            form_data.append("KnowledgeBookID", assetSet.KnowledgeBookID);
        }
        form_data.append("Name", trimData.Name);
        form_data.append("Type", assetSet.Type);

        if (classificationTags.length > 0) {
            classificationTags.forEach((id, index) => {
                form_data.append(`ClassificationTags[${index}]`, id);
            });
        }

        if (assetSet.Type === AssetSetType.Query) {
            if (assetSet.HierarchyItemQuery) {
                form_data.append("HierarchyItemQuery", assetSet.HierarchyItemQuery);
            }

            if (assetSet.AssetTagQuery) {
                form_data.append("AssetTagQuery", assetSet.AssetTagQuery);
            }
        }
        else if (assetSet.Type === AssetSetType.List) {
            if (assetSet.HierarchyItems?.length > 0) {
                assetSet.HierarchyItems.forEach((item, index) => {
                    form_data.append(`HierarchyItemIDs[${index}]`, item.ID);
                });
            }

            if (assetTagIDs.length > 0) {
                assetTagIDs.forEach((id, index) => {
                    form_data.append(`AssetTagIDs[${index}]`, id);
                });
            }
        }

        const res = await AssetSetsServices.createAssetSet(form_data, options);

        const allAssetSets = assetSet.KnowledgeBookID
            ? await AssetSetsActions.refreshAllAssetSets()
            : await AssetSetsActions.refreshFacilityAssetSets(assetSet.FacilityID, false);
        const updatedAssetSet = allAssetSets?.find((asset) => asset.ID === res.ID)
        if (!assetSet.KnowledgeBookID) {
            AssetSetVisualizationStore.notifyAssetSetsChanged(assetSet.FacilityID, res.ID);
        }
        this.beginFinish();
        return updatedAssetSet;
    },
    async deleteAssetSets(as: IAssetSet) {
        await AssetSetsServices.deleteAssetSet(as);
        // refresh data
        //if deleted asset set is selected
        if(viewerStore.getSelectedAssetSet()?.ID === as.ID) _dispatchAction(ViewerConstants.ASSET_SET_SELECT, null);
        if (as.KnowledgeBookID) {
            await AssetSetsActions.refreshAllAssetSets();
        } else {
            AssetSetVisualizationStore.notifyAssetSetsChanged(as.FacilityID, as.ID);
        }
    },
    async updateAssetSet(assetSet: IAssetSet, options?: AssetSetSaveOptions) {
        const trimData: any = FormHelper.trim(assetSet);
        const form_data: any = new FormData();
        const assetTagIDs = assetSet.AssetTagIDs ?? [];
        const classificationTags = _getValidClassificationTags(assetSet.ClassificationTags, options?.validClassificationTagIDs);

        form_data.append("ID", assetSet.ID);
        form_data.append("FacilityID", assetSet.FacilityID);
        if (assetSet.KnowledgeBookID) {
            form_data.append("KnowledgeBookID", assetSet.KnowledgeBookID);
        }
        form_data.append("VersionToken", assetSet.VersionToken);
        form_data.append("Name", trimData.Name);
        form_data.append("Type", assetSet.Type);

        if (classificationTags.length > 0) {
            classificationTags.forEach((id, index) => {
                form_data.append(`ClassificationTags[${index}]`, id);
            });
        }

        if (assetSet.Type === AssetSetType.Query) {
            if (assetSet.HierarchyItemQuery) {
                form_data.append("HierarchyItemQuery", assetSet.HierarchyItemQuery);
            }

            if (assetSet.AssetTagQuery) {
                form_data.append("AssetTagQuery", assetSet.AssetTagQuery);
            }
        }
        else if (assetSet.Type === AssetSetType.List) {
            if (assetSet.HierarchyItems?.length > 0) {
                assetSet.HierarchyItems.forEach((item, index) => {
                    form_data.append(`HierarchyItemIDs[${index}]`, item.ID);
                });
            }

            if (assetTagIDs.length > 0) {
                assetTagIDs.forEach((id, index) => {
                    form_data.append(`AssetTagIDs[${index}]`, id);
                });
            }
        }

        await AssetSetsServices.updateAssetSet(form_data, options);
        const updatedAssetSet = await AssetSetsActions.getAssetSetDetail(assetSet.ID);
        _dispatchAction(ViewerConstants.ASSET_SET_SELECT, updatedAssetSet);
        if (!assetSet.KnowledgeBookID) {
            AssetSetVisualizationStore.notifyAssetSetsChanged(assetSet.FacilityID, assetSet.ID);
        }
        this.beginFinish();
        return updatedAssetSet;
    },
}

export default AssetSetsActions;
